import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "@phosphor-icons/react";

const Hero = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, 150]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  // Staggered text animation
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <section
      id="hero"
      className="w-full min-h-[100dvh] flex flex-col justify-between px-4 sm:px-8 py-8 md:py-12 relative overflow-hidden bg-[#f5f5f5] dark:bg-[#050505] transition-colors duration-500"
    >
      {/* Top Nav */}
      <div className="w-full flex justify-between items-start z-10 relative">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col gap-1"
        >
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-black/50 dark:text-white/50">
            Lagos, NGR
          </span>
          <span className="text-[10px] font-mono text-black/40 dark:text-white/40">
            6°27′11″N 3°23′45″E
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="flex items-center gap-3 bg-white/50 dark:bg-white/5 backdrop-blur-md px-4 py-2 border border-black/10 dark:border-white/10 rounded-full"
        >
          <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.8)] animate-pulse" />
          <span className="text-[10px] uppercase font-mono tracking-widest text-black/80 dark:text-white/80">
            Available
          </span>
        </motion.div>
      </div>

      {/* Center Elegant Typography */}
      <motion.div
        style={{ y: y1, opacity }}
        variants={container}
        initial="hidden"
        animate="show"
        className="w-full flex flex-col justify-center z-10 py-20 mt-10 md:mt-0 relative"
      >
        <div className="flex flex-col items-start w-full lg:max-w-5xl mx-auto">
          <motion.div
            variants={item}
            className="mb-6 md:mb-10 flex items-center gap-4"
          >
            <div className="w-8 md:w-12 h-[1px] bg-black/30 dark:bg-white/30" />
            <span className="text-[11px] md:text-sm font-mono tracking-widest text-black/60 dark:text-white/60 uppercase">
              Ifeoluwa Adepeju — Engineer
            </span>
          </motion.div>
          <motion.h1
            variants={item}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-[76px] font-display font-medium leading-[1.1] tracking-[-0.03em] text-black dark:text-white"
          >
            Engineering digital products <br className="hidden md:block" />
            <span className="text-black/40 dark:text-white/40">
              with precision, scale, & purpose.
            </span>
          </motion.h1>

          <motion.p
            variants={item}
            className="mt-8 text-base md:text-lg text-black/60 dark:text-white/60 leading-relaxed font-sans max-w-2xl"
          >
            I specialize in architecting cross-platform mobile ecosystems and
            high-performance web interfaces. Focused on delivering secure,
            scalable, and visually exceptional experiences from complex data
            models down to the final pixel.
          </motion.p>
        </div>
      </motion.div>

      {/* Bottom Info Bar */}
      <div className="w-full flex flex-col-reverse md:flex-row justify-between items-center md:items-end gap-10 md:gap-8 z-10 pb-24 md:pb-0 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <a
            href="#projects"
            className="group flex items-center justify-center w-20 h-20 md:w-28 md:h-28 rounded-full border border-black/20 dark:border-white/20 bg-black/5 dark:bg-white/5 backdrop-blur-sm hover:bg-black dark:hover:bg-white text-black dark:text-white hover:text-white dark:hover:text-black transition-colors duration-500"
          >
            <ArrowDownRight
              size={28}
              weight="light"
              className="group-hover:translate-y-1 group-hover:translate-x-1 transition-transform duration-300"
            />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-sm text-left md:text-right"
        >
          <p className="text-[13px] md:text-sm text-black/60 dark:text-white/60 leading-relaxed font-sans">
            With 4+ years building production-grade web and mobile applications
            across fintech, SaaS, AI, and commerce platforms.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
